import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { PickList } from "primereact/picklist";
import { Button } from "primereact/button";
import { handleApiError, handleApiSuccess } from "./apiErrorHandler";
import axiosInstance from "../services/axiosInstance";
import { Dropdown } from "primereact/dropdown";

const MoveOrderDeleteDialog = ({ visible, onHide, onSaved }) => {
    const [inventory, setInventory] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [userDept, setUserDept] = useState(null);
    const [remarks, setRemarks] = useState('');
    const [selectedReason, setSelectedReason] = useState('');
    const [requireDeleteApproval, setRequireDeleteApproval] = useState(false);

    const delete_reason = [
        'User Mistake During Purchase',
        'Expired Quantity',
        'Other Mistakes',
        'Wasted During Handling',
    ];

    useEffect(() => {
        if (visible) {
            // fetch current user (to get department)
            const s = JSON.parse(localStorage.getItem('organizationSettings') || '{}');
            const userSettings = JSON.parse(localStorage.getItem('userSettings') || '{}');
            setUserDept(userSettings.department ?? null)
            if (s.require_delete_move_order_approval) {
                setRequireDeleteApproval(true);
            } else {
                setRequireDeleteApproval(false);
            }
        }
    }, [visible]);

    // Fetch full batch-level inventory 
    useEffect(() => {
        if (!visible) return; 

        if (userDept !== undefined) { 
            if (userDept) {
                axiosInstance.get(`api/inventory/?department=${userDept}&all=1`)
                    .then(res => setInventory(res.data))
                    .catch(() => setInventory([]));
            } else {
                axiosInstance.get(`api/inventory/?all=1`)
                    .then(res => setInventory(res.data))
                    .catch(() => setInventory([]));
            }
        }
    }, [userDept, visible]);

    const handleFocus = (event) => {
        // PrimeReact's PickList doesn't expose a supported way to grab the
        // target list's scroll container via a ref prop, so find it directly
        // from the element that received focus instead.
        const scrollContainer = event.target.closest('[role="listbox"]');
        if (scrollContainer) {
            scrollContainer.scrollTo({
                top: event.target.offsetTop - 50,
                behavior: "smooth",
            });
        }
    };

    // Display product + batch info
    const itemTemplate = (item) => {
        const isTarget = selectedProducts.some(prod => prod.id === item.id);
        return (
            <div className="flex flex-wrap p-2 align-items-center gap-3 w-full">
                <div className="flex-1 flex flex-column gap-2">
                    <span>
                      <b>{item.product_name}</b>
                      {" | Batch: "}{item.batch_number}
                      {" | Exp: "}{item.expiry_date}
                      {" | Price: "}{item.selling_price}
                    </span>
                </div>
                {isTarget ? (
                    <InputNumber
                        value={item.transferQuantity || item.quantity}
                        onValueChange={(e) => {
                            const updated = selectedProducts.map((prod) =>
                                prod.id === item.id ? { ...prod, transferQuantity: e.value } : prod
                            );
                            setSelectedProducts(updated);
                        }}
                        min={0}
                        max={item.quantity}
                        onFocus={handleFocus}
                        inputStyle={{ width: '100px', textAlign: 'center' }}
                    />
                ) : (
                    <span className="font-bold text-900">{item.quantity}</span>
                )}
            </div>
        );
    };

    const handleDelete = async () => {
        if (selectedProducts.length === 0 || !selectedReason || !remarks) {
            handleApiError({Error: "Please select products to delete, enter the Delete Reason & Remarks"});
            return;
        }
        const deleteOrderData = {
            source_department: userDept,
            is_delete: true,
            reason_remarks: remarks,
            order_reason: selectedReason,
            items: selectedProducts.map((item) => ({
                product: item.product,
                quantity: item.transferQuantity ?? item.quantity,
                batch_number: item.batch_number,           
                expiry_date: item.expiry_date,             
                price_list: item.price_list, 
                supplier: item.supplier,
                purchase_price: item.purchase_price            
            })),
        };

        try {
            const res = await axiosInstance.post("/api/inventory/move-order/", deleteOrderData);
            const moveOrderId = res.data.id;

            if (requireDeleteApproval) {
                const res = await axiosInstance.post(`/api/inventory/move-order/${moveOrderId}/send_for_approval/`);
                handleApiSuccess(res.data?.message || 'Sent for approval');
            } else {
                // maintain existing auto-approve behavior
                await axiosInstance.post(`/api/inventory/move-order/${moveOrderId}/approve/`);
                handleApiSuccess("Inventory deleted");
            }
            onSaved?.();
            onHide();
        } catch (err) {
            handleApiError(err);
        }
    };

    const handleCancel = () => {
        try {
        setSelectedProducts([]);
        setInventory([]);
        setRemarks('');
        setSelectedReason('');
        } finally {
        if (typeof onHide === 'function') onHide();
        }
    };

    return (
        <Dialog 
            header={
            <div>
                <div><span>Delete Inventory Items</span></div>
                <div className="flex flex-wrap align-items-center gap-2 sm:gap-2">
                    <Button
                        label={requireDeleteApproval ? "Send for Approval" : "Delete"}
                        icon={requireDeleteApproval ? "pi pi-send" : "pi pi-trash"}
                        className={requireDeleteApproval ? "p-button-warning mt-3" : "p-button-danger mt-3"}
                        onClick={handleDelete}
                        style={{ borderRadius: "6px", fontSize: "14px" }}
                    />
                    <Button label="Cancel" icon="pi pi-times" onClick={handleCancel} className="p-button-secondary mt-3" outlined style={{ borderRadius: '6px', fontSize: '14px' }} />
                </div>
            </div>
            }
            visible={visible} onHide={onHide} modal draggable={false} closable={false} maximized={true} breakpoints={{ '960px': '75vw', '641px': '90vw' }} >
            <PickList
                source={inventory}
                target={selectedProducts}
                itemTemplate={itemTemplate}
                onChange={(e) => {
                    setInventory(e.source);
                    setSelectedProducts(e.target.map(item => ({
                        ...item,
                        transferQuantity: item.transferQuantity ?? item.quantity
                    })));
                }}
                sourceHeader={
                    <div className="flex justify-between px-3 py-2 font-bold">
                        <span style={{ width: "70%" }}>Batch/Product</span>
                        <span style={{ width: "30%", textAlign: "right" }}>Quantity</span>
                    </div>
                }
                targetHeader={
                    <div className="flex justify-between px-3 py-2 font-bold">
                        <span style={{ width: "70%" }}>Batch/Product</span>
                        <span style={{ width: "30%", textAlign: "right" }}>Delete Quantity</span>
                    </div>
                }
                showSourceControls={false}
                showTargetControls
                filter
                filterBy="product_name"
            />
            <div className="grid">
                <div className="col">
                    <Dropdown
                        value={selectedReason}
                        options={delete_reason}
                        onChange={(e) => setSelectedReason(e.value)}
                        placeholder="Select Delete Reason"
                        className="mb-2"
                        style={{ width: '300px' }}
                    />
                </div>
                <div className="col">
                    <InputText
                        id="reason_text"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Enter Delete Remarks"
                        required
                        style={{ width: '300px' }}
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default MoveOrderDeleteDialog;